import { useEffect, useState } from "react";

import { LOCAL_AGENT_URL } from "@/lib/config";
import { isStandalone } from "@/lib/standalone";

import { parseAction, shouldRetry, type AgentActionName } from "./agentBridge";
import { captureFromPush, type PendingCapture } from "./captureImage";

export interface AgentPush {
  text: string;
  /**
   * A screenshot the agent is still uploading, or null.
   *
   * Present on this path and *not* on the deep link, deliberately: with a tab already listening the
   * agent sends the text first and uploads after, so there is no filename yet — only the id the
   * server will echo back on `mediaStored`. A deep link has to name a finished file instead,
   * because it is opening the tab that would have received that event.
   */
  capture: PendingCapture | null;
  /**
   * The reading log this capture belongs in, or null for whichever one is open.
   *
   * Only a **capture region** ever sets it — the tray agent's fixed rectangles, each of which may
   * pin one log (日本-463). The drag selector never does, so every ordinary capture still lands in
   * the log you are looking at.
   *
   * An extra key on the existing `search` event rather than an event of its own, for the reason
   * `SseBroadcaster` states: an older bundle ignores a key it does not know and still gets its
   * sentence, where an unknown *event name* is dropped in silence.
   */
  logId: string | null;
  /** Bumped on every push, so two identical consecutive captures are still two state changes. */
  nonce: number;
}

/**
 * The local agent's capture bridge.
 *
 * The tray agent has two ways to deliver a capture. If no Read tab is listening it opens one via a
 * deep link (`/?q=…`); if one *is* listening it pushes over this stream instead, so the sentence
 * lands in the log you are already looking at rather than in a third browser tab. `?bridge=1` is
 * what marks this subscriber as "a Read tab" — a plain `/api/events` listener must not suppress the
 * new-tab path.
 *
 * **Live in every build**, because the agent's address is a constant (see `LOCAL_AGENT_URL`). What
 * decides whether an agent is there is whether anything answers: the host writes `: connected`
 * before anything else precisely so `onopen` fires promptly, so a stream that keeps erroring
 * without ever opening means no agent on this machine, and we close for good rather than let
 * EventSource dial a dead loopback port every three seconds for the rest of the session. A phone
 * therefore pays `COLD_ATTEMPT_BUDGET` failed connections per mount and then goes quiet. An error
 * *after* an open is the agent restarting — `scripts\Update.cmd` does that routinely — and
 * EventSource's own retry handles it, indefinitely.
 *
 * The budget is why the cold case is not one-shot: the tab is often up before the agent is (session
 * restore at sign-in, or an update mid-reload), and giving up on the first failure would leave the
 * bridge dead for the session while the agent ran perfectly well seconds later.
 *
 * Safari may refuse `http://127.0.0.1` from an HTTPS page where Chrome allows it as a
 * potentially-trustworthy origin. Either way the failure is an error before open, which is the
 * branch that closes quietly, so the phone case degrades into exactly the old inert behaviour.
 */
/**
 * A screenshot the agent promised and could not deliver.
 *
 * The success signal comes from the server, which is the only party that knows the bytes landed.
 * A failure never reaches the server at all, so the agent has to say so itself — otherwise the slot
 * the tab already painted as pending stays that way for the life of the tab, disabled, with no ×
 * and nothing to click.
 */
export interface AgentShotFailure {
  captureId: string;
  message: string | null;
  nonce: number;
}

/**
 * A global Read shortcut the agent relayed (日本-710) — the mouse button that does `A` or `T` while
 * a visual novel has the focus. Read runs it through the same code its own keys run.
 */
export interface AgentAction {
  name: AgentActionName;
  /** Bumped per press, so two presses of one button are two state changes. */
  nonce: number;
}

/** What the bridge delivers: captures, the screenshots that failed to follow them, and actions. */
export interface AgentBridge {
  push: AgentPush | null;
  shotFailure: AgentShotFailure | null;
  action: AgentAction | null;
}

export function useAgentBridge(enabled = true): AgentBridge {
  const [push, setPush] = useState<AgentPush | null>(null);
  const [shotFailure, setShotFailure] = useState<AgentShotFailure | null>(null);
  const [action, setAction] = useState<AgentAction | null>(null);

  useEffect(() => {
    if (!enabled || !LOCAL_AGENT_URL) return;

    // `standalone=1` is a preference, not a second kind of bridge: every subscriber here is still
    // a bridge, and one that says nothing behaves exactly as it did before. What it buys is that
    // when a desktop shell and a leftover browser tab are both listening, the agent sends the
    // capture to the shell alone (`SseBroadcaster.DeliveryAudience`) instead of to both — which
    // is what stopped the same sentence being appended twice.
    const query = isStandalone() ? "?bridge=1&standalone=1" : "?bridge=1";
    const source = new EventSource(`${LOCAL_AGENT_URL}/api/events${query}`);
    let count = 0;
    let failures = 0;
    let actions = 0;
    let everOpened = false;
    let coldFailures = 0;

    source.addEventListener("open", () => {
      everOpened = true;
    });
    source.addEventListener("error", () => {
      if (!everOpened) coldFailures += 1;
      if (!shouldRetry(everOpened, coldFailures)) source.close();
    });
    source.addEventListener("search", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as {
          text?: unknown;
          logId?: unknown;
        };
        if (typeof data.text === "string" && data.text.trim()) {
          count += 1;
          // A malformed or absent screenshot reference is not worth dropping the sentence for —
          // `captureFromPush` answers null and the capture arrives without a picture, which is the
          // same thing that happens when the setting is off. The same goes for the log: anything
          // that is not a non-empty string means "wherever it would have gone anyway".
          const logId =
            typeof data.logId === "string" && data.logId ? data.logId : null;
          setPush({ text: data.text, capture: captureFromPush(data), logId, nonce: count });
        }
      } catch {
        // Malformed frame — drop it rather than tearing the stream down.
      }
    });
    // Spelled exactly as `SseBroadcaster.BroadcastShotFailed`. A frame nobody registered is dropped
    // in silence, and the visible result of that would be the stuck slot this event exists to
    // clear — so the failure mode of getting the name wrong is the bug itself, unchanged.
    source.addEventListener("shotFailed", (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as {
          captureId?: unknown;
          message?: unknown;
        };
        if (typeof data.captureId !== "string" || !data.captureId) return;
        failures += 1;
        setShotFailure({
          captureId: data.captureId,
          message: typeof data.message === "string" ? data.message : null,
          nonce: failures,
        });
      } catch {
        // Malformed frame — drop it rather than tearing the stream down.
      }
    });
    // Spelled exactly as `SseBroadcaster.BroadcastAction`. The agent sends it to one client only
    // (`DeliveryAudience`), so a shell and a leftover tab cannot both add the card.
    source.addEventListener("action", (event) => {
      try {
        const name = parseAction(JSON.parse((event as MessageEvent).data));
        if (!name) return;
        actions += 1;
        setAction({ name, nonce: actions });
      } catch {
        // Malformed frame — drop it rather than tearing the stream down.
      }
    });
    return () => source.close();
  }, [enabled]);

  return { push, shotFailure, action };
}
